import * as React from "react";

import type { Deal, DealStatus } from "@/api/client";
import { DealAdjudicationWorkspace } from "@/components/deals/DealAdjudicationWorkspace";
import { DealExtensionAdjudication } from "@/components/deals/DealExtensionAdjudication";
import { ManagerDealDetail } from "@/components/deals/ManagerDealDetail";
import { cn } from "@/lib/utils";

/** Review heading by deal status (CHP-223 — shared by the registration,
 * extension and closed-win reviews). */
const REVIEW_TITLE: Partial<Record<DealStatus, string>> = {
  pending_review: "Review deal registration",
  extension_pending: "Review deal extension",
  win_pending_review: "Review Closed-Won request",
};

/** Non-review states render the read-only detail view (CHP-241/243/245/246),
 * whose header is [Client Name] / [Deal ID] rather than a review action title. */
const DETAIL_STATES = new Set<DealStatus>(["live", "denied", "closed_won", "closed_lost"]);

export function reviewTitle(deal: Deal): string {
  if (DETAIL_STATES.has(deal.deal_status)) return deal.client_name;
  return REVIEW_TITLE[deal.deal_status] ?? "Deal";
}

export function reviewSubtitle(
  deal: Deal,
  partnerName?: string | null,
  leadName?: string | null,
): string {
  if (DETAIL_STATES.has(deal.deal_status)) return deal.deal_id_display ?? "";
  // Closed-won review (CHP-236): [Deal Name] – [Channel Partner] – [Lead].
  if (deal.deal_status === "win_pending_review") {
    return [deal.client_name, partnerName, leadName].filter(Boolean).join(" – ");
  }
  return partnerName ? `${deal.client_name} – ${partnerName}` : deal.client_name;
}

/** The review deadline that applies to the deal's current review state, or
 * null when no clock runs. Registration counts down to auto-approval;
 * extension and win (CHP-1176) are neutral review SLAs — neither
 * auto-finalises on breach. */
function effectiveDeadline(deal: Deal): string | null {
  if (deal.deal_status === "pending_review") return deal.sla_deadline;
  if (deal.deal_status === "extension_pending") return deal.extension_sla_deadline;
  if (deal.deal_status === "win_pending_review") return deal.win_sla_deadline ?? null;
  return null;
}

function formatCountdown(
  deadlineIso: string,
  now: Date,
): { h: number; m: number; expired: boolean } {
  const diffMs = new Date(deadlineIso).getTime() - now.getTime();
  if (diffMs <= 0) return { h: 0, m: 0, expired: true };
  const totalMin = Math.floor(diffMs / 60_000);
  return { h: Math.floor(totalMin / 60), m: totalMin % 60, expired: false };
}

/** Registration (pending_review) — inaction auto-approves the deal, so the
 * banner counts down to auto-approval (CHP-223). */
function formatAutoApprove(deadlineIso: string, now: Date): string {
  const { h, m, expired } = formatCountdown(deadlineIso, now);
  if (expired) return "Auto-approving now";
  return `Auto-approval in ${h}h ${String(m).padStart(2, "0")}m`;
}

/** Extension (extension_pending) — Flow D "SLA Temporary Extension": there is
 * NO auto-approval/rejection. On timer expiry the system grants an automatic
 * temporary extension that stays active until an admin decides, so the banner
 * is a neutral review-SLA countdown and a breach message that never says
 * "auto-approval". */
function formatExtensionSla(deadlineIso: string, now: Date): string {
  const { h, m, expired } = formatCountdown(deadlineIso, now);
  if (expired) return "SLA breached — temporary extension active until a decision is logged";
  return `Review SLA: ${h}h ${String(m).padStart(2, "0")}m remaining — the lead stays protected until you log a decision`;
}

/** Closed-win (win_pending_review) — CHP-1176: a review SLA, not an
 * auto-approval. On breach the submission is flagged and stays anchored in
 * the verification queue; nothing auto-finalises, so the copy never says
 * "auto-approval". */
function formatWinSla(deadlineIso: string, now: Date): string {
  const { h, m, expired } = formatCountdown(deadlineIso, now);
  if (expired)
    return "Review SLA breached — this win submission is awaiting a verification decision";
  return `Review SLA: ${h}h ${String(m).padStart(2, "0")}m remaining to verify this closed-won submission`;
}

/** Banner copy for the deal's current review state. */
function bannerText(deal: Deal, deadlineIso: string, now: Date): string {
  if (deal.deal_status === "extension_pending") return formatExtensionSla(deadlineIso, now);
  if (deal.deal_status === "win_pending_review") return formatWinSla(deadlineIso, now);
  return formatAutoApprove(deadlineIso, now);
}

/** Banner tone escalates as the deadline nears (CHP-223 design note: a
 * high-visibility red/amber banner demanding attention). */
function bannerTone(deadlineIso: string, now: Date): "red" | "amber" | "neutral" {
  const hoursRemaining = (new Date(deadlineIso).getTime() - now.getTime()) / 3.6e6;
  if (hoursRemaining < 6) return "red";
  if (hoursRemaining < 24) return "amber";
  return "neutral";
}

const TONE_CLASS: Record<"red" | "amber" | "neutral", string> = {
  red: "border-destructive/40 bg-destructive/10 text-destructive",
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  neutral: "border-border bg-muted/50 text-foreground",
};

export interface ManagerDealReviewProps {
  deal: Deal;
  /** Channel partner (reseller) business name — from the manager ledger row. */
  partnerName?: string | null;
  /** Channel partner lead (the deal's BDM/PO) name — from the manager ledger row. */
  leadName?: string | null;
  /**
   * Called after a successful decision (post toast + 1.5s hold). The drawer
   * passes its close handler; the standalone route passes a navigate-to-the-
   * dashboard handler (CHP-223).
   */
  onDone: () => void;
  /**
   * Another reviewer holds the review lock (collision 409 on the drawer's
   * hold attempt) — render the read-only detail instead of the adjudication
   * forms so no decision actions are available.
   */
  decisionsLocked?: boolean;
}

/**
 * The shared CM/RA/RM review body — SLA banner + the status-specific
 * adjudication form (registration / extension / closed-win). Rendered both
 * inside the right-hand review drawer (primary UX) and on the standalone
 * `/dashboard/manager/deals/$dealId` route (deep-link fallback). The title +
 * subtitle are rendered by each host (the drawer's SheetHeader / the route's
 * page header) via `reviewTitle` / `reviewSubtitle`.
 */
export function ManagerDealReview({
  deal,
  partnerName,
  leadName,
  onDone,
  decisionsLocked = false,
}: ManagerDealReviewProps) {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(id);
  }, []);

  const deadline = effectiveDeadline(deal);

  return (
    <div className="space-y-6">
      {deadline ? (
        <div
          className={cn(
            "rounded-md border p-3 text-sm font-semibold",
            TONE_CLASS[bannerTone(deadline, now)],
          )}
          data-testid="sla-banner"
        >
          {bannerText(deal, deadline, now)}
        </div>
      ) : null}

      {decisionsLocked ? (
        // Collision lock: read-only detail. The deal is in a reviewable
        // (non-LIVE) state here, so ManagerDealDetail renders no close
        // actions — onDone is wired for the type contract but never fires.
        <ManagerDealDetail deal={deal} onDone={onDone} />
      ) : deal.deal_status === "extension_pending" ? (
        <DealExtensionAdjudication
          deal={deal}
          partnerName={partnerName}
          leadName={leadName}
          onDone={onDone}
        />
      ) : deal.deal_status === "pending_review" || deal.deal_status === "win_pending_review" ? (
        <DealAdjudicationWorkspace deal={deal} partnerName={partnerName} onDone={onDone} />
      ) : (
        <ManagerDealDetail deal={deal} onDone={onDone} />
      )}
    </div>
  );
}
